import { formatClock, formatDate } from '../lib/format';
import { useI18n, type Language } from '../lib/i18n';
import { useActiveWork, type ActiveCar, type ActiveStage } from '../lib/queries';
import { Card } from './ui';

/** Same day means the clock is enough; a car left overnight needs the date. */
const localized = (
  value: { ka: string; ru: string; en: string },
  language: Language,
): string => value[language] || value.en;

function when(value: string, language: Language): string {
  const date = new Date(value);
  const sameDay = new Date().toDateString() === date.toDateString();
  return sameDay ? formatClock(date, language) : `${formatDate(date, language)} ${formatClock(date, language)}`;
}

function Stage({ stage, last }: { stage: ActiveStage; last: boolean }) {
  const { t, language } = useI18n();

  const done = stage.workStatus === 'DONE';
  const running = stage.workStatus === 'IN_PROGRESS';

  return (
    <li className="flex gap-3">
      <div className="flex flex-col items-center">
        <span
          className={`mt-1 grid size-4 shrink-0 place-items-center rounded-full border-2 ${
            done ? 'border-brand bg-brand'
              : running ? 'border-brand bg-transparent'
              : 'border-line bg-transparent'
          }`}
        >
          {running && <span className="size-1.5 animate-pulse rounded-full bg-brand" />}
        </span>
        {!last && <span className={`w-0.5 flex-1 ${done ? 'bg-brand/50' : 'bg-line'}`} />}
      </div>

      <div className={`min-w-0 flex-1 ${last ? '' : 'pb-4'}`}>
        <p className={`text-sm ${done || running ? 'text-ink' : 'text-ink-faint'}`}>
          {localized(stage.name, language)}
        </p>

        <p className="tabular mt-0.5 text-xs text-ink-faint">
          {done && stage.startedAt && stage.finishedAt
            ? `${when(stage.startedAt, language)} — ${when(stage.finishedAt, language)}`
            : running && stage.startedAt
              ? t('work.since', { time: when(stage.startedAt, language) })
              : t('work.waiting')}
        </p>
      </div>
    </li>
  );
}

function CarProgress({ car }: { car: ActiveCar }) {
  const { t, language } = useI18n();

  const done = car.stages.filter((s) => s.workStatus === 'DONE').length;
  const ready = car.status === 'READY';
  const percent = car.stages.length === 0
    ? 0
    : Math.round((done / car.stages.length) * 100);

  const vehicle = [car.make, car.model].filter(Boolean).join(' ');

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[0.66rem] font-semibold uppercase tracking-[0.14em] text-ink-faint">
            {ready ? t('work.readyTitle') : t('work.title')}
          </p>
          <p className="mt-1 font-display text-lg font-bold leading-tight text-ink">
            {vehicle || t('work.yourCar')}
          </p>
          {car.licensePlate && (
            <p className="tabular text-xs text-ink-faint">{car.licensePlate}</p>
          )}
        </div>

        {car.promisedAt && !ready && (
          <p className="tabular text-xs text-ink-faint">
            {t('work.promised', { time: when(car.promisedAt, language) })}
          </p>
        )}
        {ready && car.readyAt && (
          <p className="tabular text-xs font-semibold text-brand">
            {when(car.readyAt, language)}
          </p>
        )}
      </div>

      <div className="mt-3">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-xs text-ink-dim">
            {ready ? t('work.readyToCollect') : t('work.doneOf', { done, total: car.stages.length })}
          </span>
          <span className="tabular text-xs text-ink-faint">{percent}%</span>
        </div>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-gradient-to-r from-brand/70 to-brand transition-[width] duration-700"
            style={{ width: `${Math.max(percent, 2)}%` }}
          />
        </div>
      </div>

      {car.stages.length > 0 && (
        <ul className="mt-4">
          {car.stages.map((stage, index) => (
            <Stage
              key={`${car.reference}-${index}`}
              stage={stage}
              last={index === car.stages.length - 1}
            />
          ))}
        </ul>
      )}

      {car.acceptedAt && (
        <p className="tabular mt-1 text-[0.7rem] text-ink-faint">
          {t('work.accepted', { time: when(car.acceptedAt, language) })} · {car.reference}
        </p>
      )}
    </Card>
  );
}

/** Shown only while a car of theirs is actually in the shop. */
export function ActiveWork() {
  const { data } = useActiveWork();
  const cars = data?.items ?? [];

  if (cars.length === 0) return null;

  return (
    <div className="space-y-4">
      {cars.map((car) => <CarProgress key={car.reference} car={car} />)}
    </div>
  );
}
